'use client';

/**
 * Endpoint List Component
 * Displays a list of API endpoints with filtering and grouping
 */

import { useState, useMemo } from 'react';
import { Endpoint, HTTP_METHOD_CONFIG, HttpMethod } from '@/lib/api-contract/types';

interface EndpointListProps {
  endpoints: Endpoint[];
  selectedId?: string;
  onSelect: (endpoint: Endpoint) => void;
  onAdd: () => void;
  onDelete: (id: string) => void;
  canEdit?: boolean;
}

export function EndpointList({
  endpoints,
  selectedId,
  onSelect,
  onAdd,
  onDelete,
  canEdit = true,
}: EndpointListProps) {
  const [filter, setFilter] = useState('');
  const [methodFilter, setMethodFilter] = useState<HttpMethod | ''>('');
  const [tagFilter, setTagFilter] = useState('');

  // Get all unique tags
  const allTags = useMemo(() => {
    const tags = new Set<string>();
    endpoints.forEach((e) => e.tags?.forEach((t) => tags.add(t)));
    return Array.from(tags).sort();
  }, [endpoints]);

  // Filter endpoints
  const filteredEndpoints = useMemo(() => {
    return endpoints.filter((e) => {
      // Text filter
      if (filter) {
        const searchTerm = filter.toLowerCase();
        const matchesPath = e.path.toLowerCase().includes(searchTerm);
        const matchesSummary = e.summary?.toLowerCase().includes(searchTerm);
        const matchesOperationId = e.operationId?.toLowerCase().includes(searchTerm);
        if (!matchesPath && !matchesSummary && !matchesOperationId) return false;
      }

      // Method filter
      if (methodFilter && e.method !== methodFilter) return false;

      // Tag filter
      if (tagFilter && !e.tags?.includes(tagFilter)) return false;

      return true;
    });
  }, [endpoints, filter, methodFilter, tagFilter]);

  // Group by path prefix (first segment)
  const groupedEndpoints = useMemo(() => {
    const groups = new Map<string, Endpoint[]>();

    for (const endpoint of filteredEndpoints) {
      const segments = endpoint.path.split('/').filter(Boolean);
      const group = segments[0] || 'root';

      if (!groups.has(group)) groups.set(group, []);
      groups.get(group)!.push(endpoint);
    }

    // Sort groups and endpoints within groups
    const sorted = Array.from(groups.entries()).sort(([a], [b]) =>
      a.localeCompare(b)
    );

    for (const [, endpoints] of sorted) {
      endpoints.sort((a, b) => {
        // Sort by path, then by method
        const pathCompare = a.path.localeCompare(b.path);
        if (pathCompare !== 0) return pathCompare;
        const methodOrder = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
        return methodOrder.indexOf(a.method) - methodOrder.indexOf(b.method);
      });
    }

    return sorted;
  }, [filteredEndpoints]);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-foreground">Endpoints</h3>
          {canEdit && (
            <button
              onClick={onAdd}
              className="px-3 py-1 text-sm bg-accent text-accent-foreground rounded font-medium hover:bg-accent-hover"
            >
              + Add
            </button>
          )}
        </div>

        {/* Search */}
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search endpoints..."
          className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm focus:outline-none focus:border-accent mb-2"
        />

        {/* Filters */}
        <div className="flex gap-2">
          <select
            value={methodFilter}
            onChange={(e) => setMethodFilter(e.target.value as HttpMethod | '')}
            className="flex-1 px-2 py-1 bg-cream/10 border border-border rounded text-foreground text-sm focus:outline-none focus:border-accent"
          >
            <option value="">All Methods</option>
            {(Object.keys(HTTP_METHOD_CONFIG) as HttpMethod[]).map((method) => (
              <option key={method} value={method}>
                {method}
              </option>
            ))}
          </select>

          {allTags.length > 0 && (
            <select
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
              className="flex-1 px-2 py-1 bg-cream/10 border border-border rounded text-foreground text-sm focus:outline-none focus:border-accent"
            >
              <option value="">All Tags</option>
              {allTags.map((tag) => (
                <option key={tag} value={tag}>
                  {tag}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {filteredEndpoints.length === 0 ? (
          <div className="p-8 text-center text-foreground-tertiary">
            {endpoints.length === 0 ? (
              <>
                <p>No endpoints yet</p>
                {canEdit && (
                  <button
                    onClick={onAdd}
                    className="mt-2 text-accent hover:underline"
                  >
                    Add your first endpoint
                  </button>
                )}
              </>
            ) : (
              <p>No endpoints match your filters</p>
            )}
          </div>
        ) : (
          <div className="p-2">
            {groupedEndpoints.map(([group, endpoints]) => (
              <div key={group} className="mb-4">
                <div className="px-2 py-1 text-xs font-medium text-foreground-tertiary uppercase tracking-wider">
                  /{group}
                </div>
                <div className="space-y-1">
                  {endpoints.map((endpoint) => {
                    const methodConfig = HTTP_METHOD_CONFIG[endpoint.method];
                    const isSelected = endpoint.id === selectedId;

                    return (
                      <div
                        key={endpoint.id}
                        onClick={() => onSelect(endpoint)}
                        className={`
                          group flex items-center gap-2 px-2 py-2 rounded cursor-pointer transition-colors
                          ${isSelected
                            ? 'bg-accent-muted border border-accent/40'
                            : 'hover:bg-cream/5 border border-transparent'
                          }
                        `}
                      >
                        {/* Method badge */}
                        <span
                          className="px-2 py-0.5 text-xs font-bold rounded shrink-0"
                          style={{
                            backgroundColor: methodConfig.bgColor,
                            color: methodConfig.color,
                          }}
                        >
                          {endpoint.method}
                        </span>

                        {/* Path */}
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-mono truncate ${endpoint.deprecated ? 'line-through text-foreground-tertiary' : 'text-foreground'}`}>
                            {endpoint.path}
                          </p>
                          {endpoint.summary && (
                            <p className="text-xs text-foreground-tertiary truncate">
                              {endpoint.summary}
                            </p>
                          )}
                        </div>

                        {/* Tags */}
                        {endpoint.tags && endpoint.tags.length > 0 && (
                          <div className="hidden sm:flex gap-1">
                            {endpoint.tags.slice(0, 2).map((tag) => (
                              <span
                                key={tag}
                                className="px-1.5 py-0.5 text-xs bg-cream/10 text-foreground-tertiary rounded"
                              >
                                {tag}
                              </span>
                            ))}
                            {endpoint.tags.length > 2 && (
                              <span className="text-xs text-foreground-muted">
                                +{endpoint.tags.length - 2}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Delete button */}
                        {canEdit && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onDelete(endpoint.id);
                            }}
                            className="opacity-0 group-hover:opacity-100 p-1 text-foreground-muted hover:text-red-400 transition-all"
                            title="Delete"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer stats */}
      <div className="p-3 border-t border-border text-xs text-foreground-tertiary">
        {filteredEndpoints.length} of {endpoints.length} endpoints
      </div>
    </div>
  );
}

export default EndpointList;
